import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * MobileBottomNav — fixed 5-tab bottom navigation, only visible on small
 * screens (`<lg`). Hidden for signed-out visitors so the auth funnel still
 * leads through the top hero CTAs.
 *
 * Tabs: Today (Dashboard) · Leagues · LFG · Inbox · Me
 *  - "Inbox" + "Me" aren't real routes; they dispatch window events to
 *    let the app open the inbox/profile modals from anywhere.
 */
const ROUTE_TABS = [
  { to: "/dashboard", icon: "fa-house",          label: "Today",   testId: "mob-nav-today" },
  { to: "/leagues",   icon: "fa-trophy",         label: "Leagues", testId: "mob-nav-leagues" },
  { to: "/hqs",       icon: "fa-shield-halved",  label: "Teams",   testId: "mob-nav-teams" },
  { to: "/lfg",       icon: "fa-satellite-dish", label: "LFG",     testId: "mob-nav-lfg" },
];

export function MobileBottomNav() {
  const { user, inboxCount } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const openProfileModal = (initialTab) => {
    // Mirror the navbar avatar's behaviour from App.js (cloned `onCompleteProfile`).
    try {
      window.dispatchEvent(new CustomEvent("lh:open-profile", { detail: { tab: initialTab } }));
    } catch (_e) { /* ignore */ }
  };

  const openInbox = () => {
    // Land on dashboard then ask the host to open the inbox tab.
    navigate("/dashboard");
    setTimeout(() => openProfileModal("inbox"), 50);
  };

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-[100] backdrop-blur-md bg-black/85 border-t border-white/10 pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="flex">
        {ROUTE_TABS.map((t) => (
          <li key={t.to} className="flex-1">
            <NavLink
              to={t.to}
              data-testid={t.testId}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] tracking-widest transition-colors ${
                  isActive ? "text-[#14b8a6]" : "text-white/55 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <i className={`fas ${t.icon} text-[18px]`} />
                  <span className={isActive ? "font-bold" : ""}>{t.label.toUpperCase()}</span>
                  {isActive && (
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-gradient-to-r from-[#14b8a6] via-[#14b8a6] to-[#14b8a6]" />
                  )}
                </>
              )}
            </NavLink>
          </li>
        ))}
        <li className="flex-1">
          <button
            type="button"
            onClick={openInbox}
            data-testid="mob-nav-inbox"
            className="relative w-full flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] tracking-widest text-white/55 hover:text-white"
          >
            <span className="relative">
              <i className="fas fa-envelope text-[18px]" />
              {inboxCount > 0 && (
                <span
                  data-testid="mobile-bottom-nav-inbox-badge"
                  className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-[#14b8a6] text-black text-[9px] font-bold flex items-center justify-center"
                >
                  {inboxCount > 9 ? "9+" : inboxCount}
                </span>
              )}
            </span>
            <span>INBOX</span>
          </button>
        </li>
        <li className="flex-1">
          <button
            type="button"
            onClick={() => openProfileModal("avatar")}
            data-testid="mob-nav-me"
            className="w-full flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] tracking-widest text-white/55 hover:text-white"
          >
            <i className="fas fa-user-astronaut text-[18px]" />
            <span>ME</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default MobileBottomNav;
